import {AxiosError} from "axios";
import {useEffect, useState} from "react";
import {Trash as DeleteIcon} from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";
import {useSearchParams} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {PDFApi, PdfReplyParameters} from "../api/portal/generated";
import {AccessibleSpinner} from "../common/AccessibleSpinner";
import {RenderAlert} from "../common/RenderAlert";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

export const PDFReply = () =>
{
    /**
     * searchParams is used to get the env and hmac parameters from the reply URL
     */
    const [searchParams] = useSearchParams();
    /**
     * The env URL parameter
     */
    const [env, setEnv] = useState<string>();
    /**
     * The HMAC URL parameter
     */
    const [hmac, setHmac] = useState<string>();
    /**
     * The PDF reply parameters
     */
    const [replyParameters, setReplyParameters] = useState<PdfReplyParameters>();
    /**
     * Body of the reply
     */
    const [body, setBody] = useState<string>("");
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * If true, the dialog will be shown
     */
    const [show, setShow] = useState<boolean>(true);
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true if the message was sent
     */
    const [sent, setSent] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    useEffect(() =>
    {
        const env = searchParams.get("env");
        const hmac = searchParams.get("hmac");

        if (env === null || hmac === null)
        {
            logger.warn("env or hmac parameter missing");
            setErrorMessage(t('pdf-reply.invalidReplyURL'));

            return;
        }

        logger.debug("Validating reply parameters")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerParsePdfReplyParameters(env, hmac)
            .then(response =>
            {
                setEnv(env);
                setHmac(hmac);

                setReplyParameters(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reply parameters", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [searchParams,
        setSpinnerStateCounter,
        t]);

    const sendReply = () =>
    {
        if (env === undefined || hmac === undefined) {
            throw Error("Invalid state")
        }

        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        pdfApi.pDFControllerSendPdfReply(env, hmac, body, attachments).then(() => {
            logger.debug("Email sent")
            setSent(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error sending reply", err);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList)
    {
        setAttachments(prevAttachments => [...prevAttachments, ...Array.from(fileList)]);
        setSent(false);
    }

    function removeAttachment(file : File)
    {
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
        setSent(false);
    }

    return (
        <div>
            {!show && <h1>{t('pdf-reply.page-can-be-closed')}</h1>}

            <Modal show={show} onHide={() => setShow(false)} size="xl" fullscreen="lg-down">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('pdf-reply.title')} <AccessibleSpinner show={spinnerStateCounter > 0} />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row flex-grow-1">
                        <main className="col d-flex flex-column overflow-auto modal-main-body">
                            <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                         tooltipPlacement={"bottom"}/>

                            {replyParameters !== undefined && replyParameters.expired &&
                                <RenderAlert message={t('pdf-reply.reply-link-expired')} variant="danger"/>
                            }

                            {replyParameters !== undefined && !replyParameters.replyAllowed &&
                                <RenderAlert message={t('pdf-reply.reply-not-allowed')} variant="danger"/>
                            }

                            {sent &&
                                <RenderAlert message={t('pdf-reply.reply-sent')} variant="success"/>
                            }

                            <Form>
                                <FloatingLabel label={t('pdf-reply.subject-label')} className="text-secondary mb-3">
                                    <Form.Control type="text" value={replyParameters?.subject} placeholder="Subject"
                                                  disabled={true}/>
                                </FloatingLabel>

                                <FloatingLabel label={t('pdf-reply.from-label')} className="text-secondary mb-3">
                                    <Form.Control type="text" value={replyParameters?.from} placeholder="From"
                                                  disabled={true}/>
                                </FloatingLabel>

                                <FloatingLabel label={t('pdf-reply.to-label')} className="text-secondary mb-3">
                                    <Form.Control type="text" value={replyParameters?.to} placeholder="To"
                                                  disabled={true}/>
                                </FloatingLabel>

                                {!StringUtils.isBlank(replyParameters?.cc) &&
                                    <FloatingLabel label={t('pdf-reply.cc-label')} className="text-secondary mb-3">
                                        <Form.Control type="text" value={replyParameters?.cc} placeholder="Cc"
                                                      disabled={true}/>
                                    </FloatingLabel>
                                }

                                <Form.Group className="mb-3">
                                    <Form.Label>{t('pdf-reply.attachment-label')}</Form.Label>&nbsp;{replyParameters !== undefined &&
                                            <span className="text-secondary">(max. {Utils.formatBytes(replyParameters?.attachmentsMaxSize)})</span>}
                                    <Form.Control
                                        size="sm"
                                        type="file"
                                        multiple={true}
                                        accept=""
                                        onChange={e => addAttachments(((e.target as HTMLInputElement).files as FileList))}
                                    />
                                </Form.Group>

                                <ListGroup>
                                    {attachments?.map(file => (
                                        <ListGroup.Item key={file.name} className="border-0">
                                            <DeleteIcon title={t('pdf-reply.remove-attachment-title')} onClick={() => {removeAttachment(file)}}/> {file.name}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>

                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary">{t('pdf-reply.body-label')}</Form.Label>&nbsp;{replyParameters !== undefined &&
                                            <span className="text-secondary">(max. {Utils.formatBytes(replyParameters?.bodyMaxSize, 0)})</span>}
                                    <Form.Control
                                        className="font-monospace white-space-pre"
                                        as="textarea"
                                        onChange={e => {setBody(e.target.value); setSent(false)}}
                                        value={body}
                                        rows={8}
                                        autoFocus
                                    />
                                </Form.Group>
                            </Form>
                        </main>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success"
                            onClick={sendReply}
                            disabled={replyParameters === undefined || StringUtils.isBlank(body)}>
                        {sending && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {sending ? t('pdf-reply.sending') : t('pdf-reply.send')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}